import { supabase, isSupabaseReady } from '@/integrations/supabase/client';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types-tenant';
import { getFallbackTenant } from '@/services/tenantFallbacks';

export type Tenant = Tables<'tenants'>;
export type TenantInsert = TablesInsert<'tenants'>;
export type TenantUpdate = TablesUpdate<'tenants'>;

export class TenantService {
  /**
   * Get tenant by subdomain
   * Permanent resilience: if Supabase is not ready or fetch fails, return a known fallback tenant for
   * recognized subdomains (e.g., virtuzone) so the tenant site remains functional.
   */
  static async getTenantBySubdomain(subdomain: string): Promise<Tenant | null> {
    try {
      if (isSupabaseReady) {
        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .eq('subdomain', subdomain)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No tenant in DB – check fallbacks before giving up
            const fb = getFallbackTenant(subdomain);
            return (fb as unknown as Tenant) || null;
          }
          // Other Supabase error – fall back
          console.warn('[TenantService] Supabase error, using fallback where available:', error);
          const fb = getFallbackTenant(subdomain);
          if (fb) return fb as unknown as Tenant;
          throw new Error(`Failed to fetch tenant: ${error.message}`);
        }

        if (data) return data as Tenant;
      } else {
        console.warn('[TenantService] Supabase not ready. Using fallback if available.');
        const fb = getFallbackTenant(subdomain);
        if (fb) return fb as unknown as Tenant;
      }
    } catch (e) {
      console.warn('[TenantService] Exception fetching tenant. Using fallback if available.', e);
      const fb = getFallbackTenant(subdomain);
      if (fb) return fb as unknown as Tenant;
      // Rethrow minimally so callers can show message
      throw e instanceof Error ? e : new Error('Failed to fetch tenant');
    }

    // Nothing found
    return null;
  }

  /**
   * Get all tenants (for super admin)
   */
  static async getAllTenants(): Promise<Tenant[]> {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch tenants: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Create a new tenant
   */
  static async createTenant(tenant: TenantInsert): Promise<Tenant> {
    try {
      // Check if Supabase is properly configured
      if (!supabase) {
        throw new Error('Supabase client is not initialized. Please check your environment variables.');
      }

      const { data, error } = await supabase
        .from('tenants')
        .insert(tenant)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);

        // Provide more specific error messages
        if (error.code === '42P01') {
          throw new Error('Tenants table does not exist. Please run the database migration first.');
        } else if (error.code === '23505') {
          throw new Error('A tenant with this subdomain already exists.');
        } else if (error.code === '42501') {
          throw new Error(
            'Row Level Security blocked the insert into tenants (code 42501). In Supabase SQL Editor, run database-migrations/003_public_policies_and_seed_audits.sql and 004_allow_public_insert_tenants.sql (DEV only), or create an authenticated admin-only INSERT policy.'
          );
        } else if (error.message.includes('JWT')) {
          throw new Error('Authentication error. Please check your Supabase configuration.');
        } else {
          throw new Error(`Failed to create tenant: ${error.message} (Code: ${error.code})`);
        }
      }

      if (!data) {
        throw new Error('No data returned from tenant creation');
      }

      // Copy default products and questions for the new tenant
      await this.copyDefaultProductsForTenant(data.id);

      return data;
    } catch (err) {
      console.error('Error in createTenant:', err);
      if (err instanceof Error) {
        throw err;
      } else {
        throw new Error('Unknown error occurred while creating tenant');
      }
    }
  }

  /**
   * Update tenant
   */
  static async updateTenant(id: string, updates: TenantUpdate): Promise<Tenant> {
    console.log('Updating tenant with ID:', id, 'Updates:', updates);

    // First check if the tenant exists
    const { data: existingTenant, error: fetchError } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', id)
      .single();

    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        throw new Error(`Tenant with ID ${id} not found`);
      }
      throw new Error(`Failed to fetch tenant: ${fetchError.message}`);
    }

    console.log('Found existing tenant:', existingTenant);

    const { data, error } = await supabase
      .from('tenants')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Update error details:', error);
      throw new Error(`Failed to update tenant: ${error.message} (Code: ${error.code})`);
    }

    if (!data) {
      throw new Error('No data returned from update operation');
    }

    console.log('Successfully updated tenant:', data);
    return data;
  }

  /**
   * Delete tenant
   */
  static async deleteTenant(id: string): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete tenant: ${error.message}`);
    }
  }

  /**
   * Copy default products and questions for a new tenant
   */
  private static async copyDefaultProductsForTenant(tenantId: string): Promise<void> {
    try {
      // Call the database function to copy default products and questions
      const { error } = await supabase.rpc('copy_default_products_for_tenant', {
        new_tenant_id: tenantId
      });

      if (error) {
        console.error('Failed to copy default products for tenant:', error);
        // Don't throw here as the tenant was already created successfully
        // This is a background operation that can be retried
      }
    } catch (err) {
      console.error('Error copying default products for tenant:', err);
    }
  }

  /**
   * Manually copy default products for an existing tenant (public method)
   */
  static async copyProductsForTenant(tenantId: string): Promise<void> {
    return this.copyDefaultProductsForTenant(tenantId);
  }


  /**
   * Simple client-side health check for tenant creation capabilities
   * - Checks SELECT on tenants
   * - Attempts INSERT + DELETE of a temporary healthcheck tenant
   */
  static async runHealthCheck(): Promise<{
    ok: boolean;
    details: { select: boolean; insert: boolean; del: boolean };
    messages: string[];
  }> {
    const messages: string[] = [];
    const details = { select: false, insert: false, del: false };

    try {
      // 1) SELECT check
      const { error: selErr } = await supabase.from('tenants').select('id').limit(1);
      if (!selErr) {
        details.select = true;
        messages.push('SELECT on tenants: OK');
      } else {
        messages.push(`SELECT on tenants failed: ${selErr.message}`);
      }

      // 2) INSERT a temporary tenant
      const suffix = Math.random().toString(36).slice(2, 8);
      const temp = {
        name: `Health Check ${suffix}`,
        subdomain: `healthcheck-${suffix}`,
        logo_url: '',
        color_scheme_json: null,
        banner_config_json: null,
      } as TenantInsert;

      const { data: insData, error: insErr } = await supabase
        .from('tenants')
        .insert(temp)
        .select()
        .single();

      if (!insErr && insData) {
        details.insert = true;
        messages.push('INSERT into tenants: OK');

        // 3) DELETE the temporary tenant
        const { error: delErr } = await supabase
          .from('tenants')
          .delete()
          .eq('id', insData.id);
        if (!delErr) {
          details.del = true;
          messages.push('DELETE temporary tenant: OK');
        } else {
          messages.push(`DELETE temporary tenant failed: ${delErr.message}`);
        }
      } else {
        messages.push(`INSERT into tenants failed: ${insErr?.message || 'unknown error'}`);
      }

      const ok = details.select && details.insert && details.del;
      return { ok, details, messages };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      messages.push(`Health check exception: ${msg}`);
      return { ok: false, details, messages };
    }
  }

  /**
   * Get tenant statistics
   */
  static async getTenantStats(tenantId: string): Promise<{
    leadsCount: number;
    submissionsCount: number;
    productsCount: number;
  }> {
    const [leadsResult, submissionsResult, productsResult] = await Promise.all([
      supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
      supabase
        .from('submissions')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
      supabase
        .from('products')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
    ]);

    return {
      leadsCount: leadsResult.count || 0,
      submissionsCount: submissionsResult.count || 0,
      productsCount: productsResult.count || 0,
    };
  }

  /**
   * Update tenant color scheme
   */
  static async updateTenantColorScheme(
    tenantId: string,
    colorScheme: Record<string, string>
  ): Promise<Tenant> {
    return this.updateTenant(tenantId, {
      color_scheme_json: colorScheme,
      updated_at: new Date().toISOString(),
    });
  }

  /**
   * Update tenant logo
   */
  static async updateTenantLogo(tenantId: string, logoUrl: string): Promise<Tenant> {
    return this.updateTenant(tenantId, {
      logo_url: logoUrl,
      updated_at: new Date().toISOString(),
    });
  }
}